import { getBackendAPIClient } from "@lib/backendAPIClient";
import { organizationClientContract } from "@common/contracts/organization-client.contract";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "queryClient";
import { z } from "zod";
import { WorkspaceDto } from "@common/dto/workspace/workspace.dto";
import {
    updateWorkspaceDetailsInQueryData,
    workspaceQueryKeys,
} from "./workspace.api";
import { billingOrganizationQueryKeys } from "./billingOrganization.api";

const organizationClientContractClient = getBackendAPIClient(
    organizationClientContract,
);

export const organizationClientQueryKeys = {
    all: () => ["organization-client"],
    demoWorkspace: (organizationId: string) => [
        ...organizationClientQueryKeys.all(),
        "demo-workspace",
        organizationId,
    ],
} as const;

export function useOrganizationDemoWorkspaceQuery(organizationId?: string) {
    return useQuery({
        queryKey: organizationClientQueryKeys.demoWorkspace(
            organizationId ?? "",
        ),
        queryFn: async () => {
            const result =
                await organizationClientContractClient.getDemoWorkspace({
                    params: { organizationId: organizationId ?? "" },
                });

            return result.body.demoWorkspace;
        },
        enabled: Boolean(organizationId),
        staleTime: (query) => (query.state.data ? 1000 * 60 * 5 : 0),
    });
}

type CreateOrganizationClientPayload = z.infer<
    typeof organizationClientContract.createClient.body
>;

type RecordClientAccessPayload = z.infer<
    typeof organizationClientContract.recordClientAccess.body
>;

interface RecordOrganizationClientAccessParams {
    organizationId: string;
    payload: RecordClientAccessPayload;
}

/**
 * Fire-and-forget audit write recording that an organization member entered a
 * client workspace. Not a React Query mutation — callers must swallow errors
 * (`void recordOrganizationClientAccess(...).catch(() => undefined)`) so it
 * never blocks navigation.
 */
export async function recordOrganizationClientAccess({
    organizationId,
    payload,
}: RecordOrganizationClientAccessParams): Promise<void> {
    await organizationClientContractClient.recordClientAccess({
        params: { organizationId },
        body: payload,
    });
}

type CreateFirmWorkspacePayload = z.infer<
    typeof organizationClientContract.createFirmWorkspace.body
>;

export type UpdateOrganizationClientPayload = z.infer<
    typeof organizationClientContract.updateClient.body
>;

type UpdateClientEntityPayload = z.infer<
    typeof organizationClientContract.updateClientEntity.body
>;

type PatchOrganizationClientPayload = z.infer<
    typeof organizationClientContract.patchClient.body
>;

interface CreateOrganizationClientParams {
    organizationId: string;
    payload: CreateOrganizationClientPayload;
}

interface CreateFirmWorkspaceParams {
    organizationId: string;
    payload: CreateFirmWorkspacePayload;
}

interface UpdateOrganizationClientParams {
    organizationId: string;
    clientId: string;
    payload: UpdateOrganizationClientPayload;
}

export function useCreateOrganizationClientMutation() {
    return useMutation({
        mutationFn: async ({
            organizationId,
            payload,
        }: CreateOrganizationClientParams) => {
            const result = await organizationClientContractClient.createClient({
                params: {
                    organizationId,
                },
                body: payload,
            });

            return result.body.clientId;
        },
        onSuccess(_, { organizationId }) {
            queryClient.invalidateQueries({
                queryKey: workspaceQueryKeys.all(),
            });
            queryClient.invalidateQueries({
                queryKey:
                    billingOrganizationQueryKeys.organizationOwnedPlanPrices(
                        organizationId,
                    ),
            });
        },
    });
}

export function useCreateFirmWorkspaceMutation() {
    return useMutation({
        mutationFn: async ({
            organizationId,
            payload,
        }: CreateFirmWorkspaceParams) => {
            await organizationClientContractClient.createFirmWorkspace({
                params: {
                    organizationId,
                },
                body: payload,
            });
        },
        onSuccess() {
            queryClient.invalidateQueries({
                queryKey: workspaceQueryKeys.all(),
            });
            queryClient.invalidateQueries({
                queryKey: organizationClientQueryKeys.all(),
            });
        },
    });
}

export function useUpdateOrganizationClientMutation() {
    return useMutation({
        mutationFn: async ({
            organizationId,
            clientId,
            payload,
        }: UpdateOrganizationClientParams) => {
            const result = await organizationClientContractClient.updateClient({
                params: {
                    organizationId,
                    clientId,
                },
                body: payload,
            });

            return WorkspaceDto.deserialize(result.body);
        },
        async onSuccess(updatedClient, { clientId, organizationId }) {
            updateWorkspaceDetailsInQueryData(clientId, (workspaceDetails) => ({
                ...workspaceDetails,
                workspace: {
                    ...workspaceDetails.workspace,
                    ...updatedClient,
                    // Preserve loaded relations from cache (not returned by client update endpoint)
                    entities: workspaceDetails.workspace.entities ?? [],
                    userAssignments:
                        workspaceDetails.workspace.userAssignments ?? [],
                },
            }));

            await queryClient.invalidateQueries({
                queryKey: workspaceQueryKeys.allQueries(),
            });
            await queryClient.invalidateQueries({
                queryKey:
                    billingOrganizationQueryKeys.organizationOwnedPlanPrices(
                        organizationId,
                    ),
            });
        },
    });
}

interface UpdateClientEntityParams {
    organizationId: string;
    clientId: string;
    entityId: number;
    payload: UpdateClientEntityPayload;
}

export function useUpdateClientEntityMutation() {
    return useMutation({
        mutationFn: async ({
            organizationId,
            clientId,
            entityId,
            payload,
        }: UpdateClientEntityParams) => {
            const result =
                await organizationClientContractClient.updateClientEntity({
                    params: {
                        organizationId,
                        clientId,
                        entityId,
                    },
                    body: payload,
                });

            return result.body;
        },
        onSuccess() {
            queryClient.invalidateQueries({
                queryKey: workspaceQueryKeys.allQueries(),
            });
        },
    });
}

interface PatchOrganizationClientParams {
    organizationId: string;
    clientId: string;
    payload: PatchOrganizationClientPayload;
}

export function usePatchOrganizationClientMutation() {
    return useMutation({
        mutationFn: async ({
            organizationId,
            clientId,
            payload,
        }: PatchOrganizationClientParams) => {
            await organizationClientContractClient.patchClient({
                params: {
                    organizationId,
                    clientId,
                },
                body: payload,
            });
        },
        onSuccess(_, { clientId, organizationId, payload }) {
            if (payload.note !== undefined) {
                updateWorkspaceDetailsInQueryData(
                    clientId,
                    (workspaceDetails) => ({
                        ...workspaceDetails,
                        organizationAssignments:
                            workspaceDetails.organizationAssignments.map(
                                (assignment) => ({
                                    ...assignment,
                                    note: payload.note ?? assignment.note,
                                }),
                            ),
                    }),
                );
            }

            queryClient.invalidateQueries({
                queryKey: workspaceQueryKeys.allQueries(),
            });
            queryClient.invalidateQueries({
                queryKey:
                    billingOrganizationQueryKeys.organizationOwnedPlanPrices(
                        organizationId,
                    ),
            });
        },
    });
}

interface DeleteClientParams {
    organizationId: string;
    clientId: string;
}

export function useDeleteClientMutation() {
    return useMutation({
        mutationFn: async ({
            organizationId,
            clientId,
        }: DeleteClientParams) => {
            await organizationClientContractClient.deleteClient({
                params: { organizationId, clientId },
            });
        },
        onSuccess(_, { organizationId }) {
            queryClient.invalidateQueries({
                queryKey: workspaceQueryKeys.all(),
            });
            queryClient.invalidateQueries({
                queryKey:
                    billingOrganizationQueryKeys.organizationOwnedPlanPrices(
                        organizationId,
                    ),
            });
        },
    });
}

interface RemoveClientParams {
    organizationId: string;
    clientId: string;
}

export function useRemoveClientMutation() {
    return useMutation({
        mutationFn: async ({
            organizationId,
            clientId,
        }: RemoveClientParams) => {
            await organizationClientContractClient.removeClient({
                params: { organizationId },
                body: { clientId },
            });
        },
        onSuccess(_, { organizationId }) {
            queryClient.invalidateQueries({
                queryKey: workspaceQueryKeys.all(),
            });
            queryClient.invalidateQueries({
                queryKey:
                    billingOrganizationQueryKeys.organizationOwnedPlanPrices(
                        organizationId,
                    ),
            });
        },
    });
}

interface TransferBillingAndRemoveClientParams {
    organizationId: string;
    clientId: string;
    newPrimaryAdminId: number;
}

export function useTransferBillingAndRemoveClientMutation() {
    return useMutation({
        mutationFn: async ({
            organizationId,
            clientId,
            newPrimaryAdminId,
        }: TransferBillingAndRemoveClientParams) => {
            await organizationClientContractClient.transferBillingAndRemoveClient(
                {
                    params: { organizationId },
                    body: { clientId, newPrimaryAdminId },
                },
            );
        },
        onSuccess(_, { organizationId }) {
            queryClient.invalidateQueries({
                queryKey: workspaceQueryKeys.all(),
            });
            queryClient.invalidateQueries({
                queryKey:
                    billingOrganizationQueryKeys.organizationOwnedPlanPrices(
                        organizationId,
                    ),
            });
        },
    });
}
